import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  useToast,
  Text,
  Alert,
  AlertIcon,
  Icon,
  Divider,
  Badge,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Box,
} from '@chakra-ui/react';
import { FiList, FiFilter } from 'react-icons/fi';
import { Select } from 'chakra-react-select';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Smart List Builder Modal
 * 
 * Simple filter builder for creating Smart Lists (Phase 1).
 * 
 * Filters:
 * - Contact types (multi-select)
 * - Tags (multi-select)
 * - Owner (for admins)
 * - Has active deals
 * - Last contacted date range
 * - Total deals range
 * 
 * Phase 2 will add advanced AND/OR logic.
 */
export default function SmartListBuilderModal({
  isOpen,
  onClose,
  onSave,
  existingList = null,
}) {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const toast = useToast();
  const { contactTypes, contactTags } = useCrmSettings();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [selectedContactTypes, setSelectedContactTypes] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [hasActiveDeals, setHasActiveDeals] = useState(null); // null = no filter
  const [totalDealsMin, setTotalDealsMin] = useState('');
  const [totalDealsMax, setTotalDealsMax] = useState('');
  const [lastContactedAfter, setLastContactedAfter] = useState('');
  const [lastContactedBefore, setLastContactedBefore] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  useEffect(() => {
    if (existingList) {
      setName(existingList.name || '');
      setDescription(existingList.description || '');
      setIsGlobal(existingList.isGlobal || false);
      
      const filters = existingList.filters || {};
      
      if (filters.contactTypes) {
        setSelectedContactTypes(
          filters.contactTypes.map(val => ({
            value: val,
            label: contactTypes?.find(t => t.value === val)?.label || val,
          }))
        );
      }
      
      if (filters.tags) {
        setSelectedTags(
          filters.tags.map(val => ({
            value: val,
            label: contactTags?.find(t => t.value === val)?.label || val,
          }))
        );
      }
      
      setHasActiveDeals(filters.hasActiveDeals);
      setTotalDealsMin(filters.totalDealsMin || '');
      setTotalDealsMax(filters.totalDealsMax || '');
      setLastContactedAfter(filters.lastContactedAfter || '');
      setLastContactedBefore(filters.lastContactedBefore || '');
    } else {
      // Reset form
      setName('');
      setDescription('');
      setIsGlobal(false);
      setSelectedContactTypes([]);
      setSelectedTags([]);
      setHasActiveDeals(null);
      setTotalDealsMin('');
      setTotalDealsMax('');
      setLastContactedAfter('');
      setLastContactedBefore('');
    }
  }, [existingList, isOpen, contactTypes, contactTags]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter a name for this Smart List',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Build filters object
    const filters = {};
    
    if (selectedContactTypes.length > 0) {
      filters.contactTypes = selectedContactTypes.map(t => t.value);
    }
    
    if (selectedTags.length > 0) {
      filters.tags = selectedTags.map(t => t.value);
    }
    
    if (hasActiveDeals !== null) {
      filters.hasActiveDeals = hasActiveDeals;
    }
    
    if (totalDealsMin !== '') {
      filters.totalDealsMin = parseInt(totalDealsMin);
    }
    
    if (totalDealsMax !== '') {
      filters.totalDealsMax = parseInt(totalDealsMax);
    }
    
    if (lastContactedAfter) {
      filters.lastContactedAfter = new Date(lastContactedAfter).toISOString();
    }
    
    if (lastContactedBefore) {
      filters.lastContactedBefore = new Date(lastContactedBefore).toISOString();
    }

    const listData = {
      name: name.trim(),
      description: description.trim(),
      isGlobal,
      filters,
      sortBy: 'stats.lastContactDate',
      sortDirection: 'desc',
    };

    setIsSaving(true);
    try {
      if (onSave) {
        await onSave(listData, existingList?.id);
      }

      toast({
        title: existingList ? 'Smart List updated' : 'Smart List created',
        status: 'success',
        duration: 2000,
      });

      onClose();
    } catch (error) {
      console.error('Error saving Smart List:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Count active filters
  const activeFilterCount = [
    selectedContactTypes.length > 0,
    selectedTags.length > 0,
    hasActiveDeals !== null,
    totalDealsMin !== '',
    totalDealsMax !== '',
    lastContactedAfter,
    lastContactedBefore,
  ].filter(Boolean).length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FiList} />
            <Text>{existingList ? 'Edit' : 'Create'} Smart List</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Name & Description */}
            <FormControl isRequired>
              <FormLabel fontSize="sm">List Name</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., My Hot Leads"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Description (Optional)</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this list contains..."
                rows={2}
              />
            </FormControl>

            {isCRMAdmin && (
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel fontSize="sm" mb={0}>Share with Team (Global)</FormLabel>
                  <Text fontSize="xs" color="gray.600">
                    Make this list available to all CRM users
                  </Text>
                </Box>
                <Switch
                  isChecked={isGlobal}
                  onChange={(e) => setIsGlobal(e.target.checked)}
                  colorScheme="purple"
                  size="lg"
                />
              </FormControl>
            )}

            <Divider />

            {/* Filters Section */}
            <Box>
              <HStack justify="space-between" mb={3}>
                <HStack spacing={2}>
                  <Icon as={FiFilter} color="blue.500" />
                  <Text fontWeight="semibold">Filters</Text>
                </HStack>
                <Badge colorScheme="blue">
                  {activeFilterCount} active
                </Badge>
              </HStack>

              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm">Contact Types</FormLabel>
                  <Select
                    isMulti
                    value={selectedContactTypes}
                    onChange={setSelectedContactTypes}
                    options={contactTypes?.map(type => ({
                      value: type.value,
                      label: type.label,
                    })) || []}
                    placeholder="All types..."
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Tags</FormLabel>
                  <Select
                    isMulti
                    value={selectedTags}
                    onChange={setSelectedTags}
                    options={contactTags?.map(tag => ({
                      value: tag.value,
                      label: tag.label,
                    })) || []}
                    placeholder="All tags..."
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Has Active Deals</FormLabel>
                  <HStack spacing={4}>
                    <Button
                      size="sm"
                      variant={hasActiveDeals === null ? 'solid' : 'outline'}
                      colorScheme={hasActiveDeals === null ? 'blue' : 'gray'}
                      onClick={() => setHasActiveDeals(null)}
                    >
                      Any
                    </Button>
                    <Button
                      size="sm"
                      variant={hasActiveDeals === true ? 'solid' : 'outline'}
                      colorScheme={hasActiveDeals === true ? 'blue' : 'gray'}
                      onClick={() => setHasActiveDeals(true)}
                    >
                      Yes
                    </Button>
                    <Button
                      size="sm"
                      variant={hasActiveDeals === false ? 'solid' : 'outline'}
                      colorScheme={hasActiveDeals === false ? 'blue' : 'gray'}
                      onClick={() => setHasActiveDeals(false)}
                    >
                      No
                    </Button>
                  </HStack>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Total Deals Range</FormLabel>
                  <HStack>
                    <NumberInput
                      value={totalDealsMin}
                      onChange={(val) => setTotalDealsMin(val)}
                      min={0}
                    >
                      <NumberInputField placeholder="Min" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <Text>to</Text>
                    <NumberInput
                      value={totalDealsMax}
                      onChange={(val) => setTotalDealsMax(val)}
                      min={0}
                    >
                      <NumberInputField placeholder="Max" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </HStack>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Last Contacted Date Range</FormLabel>
                  <HStack>
                    <Input
                      type="date"
                      value={lastContactedAfter}
                      onChange={(e) => setLastContactedAfter(e.target.value)}
                      placeholder="After"
                      size="sm"
                    />
                    <Text>to</Text>
                    <Input
                      type="date"
                      value={lastContactedBefore}
                      onChange={(e) => setLastContactedBefore(e.target.value)}
                      placeholder="Before"
                      size="sm"
                    />
                  </HStack>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Leave blank for no date restriction
                  </Text>
                </FormControl>
              </VStack>
            </Box>

            <Alert status="info" size="sm" borderRadius="md">
              <AlertIcon />
              <Text fontSize="xs">
                Preview and advanced query builder coming in Phase 2. For now, save and test your filters.
              </Text>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
          >
            {existingList ? 'Update List' : 'Create List'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

